import React from "react";
import styled from "styled-components";
import {margin, width, padding, letterSpacing} from "styled-system";
import { Flex, Text } from "../../assets/ui";

const Input = styled.input`
  padding: 11px 13px 14px;
  background: #ffffff;
  border: 1px solid #e5eced;
  box-sizing: border-box;
  box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.06);
  border-radius: 2px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  color: #233247;
  ${padding};
  ${letterSpacing};
`;

const Label = styled.label`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  flex-direction: column;
  color: #586981;
  margin-bottom: 16px;
  position: relative;
  ${margin};
  ${width};
`;

const StyledRange = styled.div`
  font-weight: bold;
  font-size: 18px;
  line-height: 22px;
  display: flex;
  flex-direction: column;
  color: #ed3163;
  margin-bottom: 20px;
  & > .label {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 17px;
    margin-bottom: 18px;
    color: #586981;
  }

  & input {
    margin-right: 14px;
    -webkit-appearance: none;
    position: relative;
    overflow: hidden;
    height: 30px;
    width: 200px;
    cursor: pointer;
    background: #f3f8ff;
    border-radius: 16px;
    transition: box-shadow 0.2s ease-in-out;
    
    &::-webkit-slider-runnable-track {
      background: #ddd;
    }

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 30px; /* 1 */
      height: 30px;
      background: #fff;
      border-radius: 50%;
      box-shadow: -100px 0 0 85px #ed3163;
    }

    &::-moz-range-track {
      height: 40px;
      background: #ddd;
    }

    &::-moz-range-thumb {
      background: #fff;
      height: 30px;
      width: 30px;
      border-radius: 50%;
      box-shadow: -100vw 0 0 99vw #ed3163;
      box-sizing: border-box;
    }

    &::-ms-fill-lower {
      background: dodgerblue;
    }

    &::-ms-thumb {
      background: #fff;
      border: 2px solid #999;
      height: 40px;
      width: 20px;
      box-sizing: border-box;
    }

    &::-ms-ticks-after {
      display: none;
    }

    &::-ms-ticks-before {
      display: none;
    }

    &::-ms-track {
      background: red;
      color: transparent;
      height: 40px;
      border: none;
    }

    &::-ms-tooltip {
      display: none;
    }
  }
`;

const Textarea = styled.textarea`
  padding: 11px 13px 14px;
  background: #ffffff;
  border: 1px solid #e5eced;
  box-sizing: border-box;
  box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.06);
  border-radius: 2px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  color: #233247;
  resize: none;
`;

const StyledCheckbox = styled.label`
  display: flex;
  align-items: center;
  min-width: 125px;
  margin-bottom: 16px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;

  color: #586981;
  position: relative;
  padding-left: 35px;
  min-height: 25px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  & input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  & .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 25px;
    width: 25px;
    background: #ffffff;
    border: 1px solid #e5eced;
    box-sizing: border-box;
    box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.06);
    border-radius: 2px;
    transition: 0.5s;
  }

  & input:checked ~ .checkmark {
    border: 1px solid #ed3163;
  }

  & .checkmark:after {
    content: "";
    position: absolute;
    display: none;
    transition: 0.5s;
  }

  & input:checked ~ .checkmark:after {
    display: block;
  }

  & .checkmark:after {
    left: 5px;
    top: 5px;
    width: 14px;
    height: 14px;
    background: #ed3163;
    border-radius: 2px;
  }
`;

const StyledRadio = styled.label`
  background: ${({ checked }) => (checked ? "#fff" : "#F3F8FF")};
  font-style: normal;
  font-weight: ${({ checked }) => (checked ? "bold" : "normal")};
  font-size: 14px;
  margin-right: 8px;
  min-width: 72px;
  transition: 0.5s;
  margin-bottom: 8px;
  cursor: pointer;
  line-height: 19px;
  text-align: center;
  text-transform: uppercase;
  padding: 12px 19px;
  border: 1px solid ${({ checked }) => (checked ? "#ED3163" : "transparent")};
  border-radius: 26px;
  color: ${({ checked }) => (checked ? "#ED3163" : "#586981")};
`;

export const Field = ({ isTextarea = false, label, labelStyle, wrapperStyle, icon, ...inputProps }) => {
  return (
    <Label {...wrapperStyle}>
        {icon}
      {label && <Text marginBottom={["12px"]} {...labelStyle}>{label}</Text>}
      {isTextarea ? (
        <Textarea {...inputProps} rows="3" />
      ) : (
        <Input {...inputProps} />
      )}
    </Label>
  );
};

export const Range = ({ isTextarea = false, label, value, ...inputProps }) => {
  return (
    <StyledRange>
      <span className="label">{label}</span>

      <Flex alignItems="center">
        <input
          type="range"
          min="1"
          max="10"
          step="1"
          {...inputProps}
          value={value}
        />
        {value}
      </Flex>
    </StyledRange>
  );
};

export const CheckBox = ({
  isTextarea = false,
  label,
  name,
  value,
  arrayHelpers
}) => {
  return (
    <StyledCheckbox>
      {label}
      <input
        type="checkbox"
        checked={value}
        onChange={() => {
          let drugs = arrayHelpers.form.values.drugs;
          if (drugs.includes(name)) {
            drugs.forEach((drug, index) => {
              if (drug === name) {
                arrayHelpers.remove(index);
              }
            });
          } else {
            arrayHelpers.push(name);
          }
        }}
      />
      <span className="checkmark" />
    </StyledCheckbox>
  );
};

export const Radio = ({
  isTextarea = false,
  label,
  name,
  checked,
  value,
  setFieldValue,
  ...inputProps
}) => {
  return (
    <StyledRadio checked={checked} onClick={() => setFieldValue(name, value)}>
      {value}
    </StyledRadio>
  );
};
